<script lang="ts" setup>
	import { useForm } from 'ant-design-vue/es/form'
	import type { DefaultOptionType, SelectValue } from 'ant-design-vue/es/select'
	import type { ListContext } from '~/components/Shared/list-item.vue'
	import { useScrollPaginator } from '~/composables/useScrollPaginator'
	import useProductActivationApi from '~/services/apis/product-activation'
	import useEmployeesApi from '~/services/apis/employeesApi'
	import { useResourcesApi } from '~/services/apis/resources'
	import type { RecordSchema, SelectOptionItem } from '@/types'

	const props = defineProps<{
		categoryType: string
		productCode?: number
		purchaseId: string
		customerId: string
		policyInfo?: {
			first_name: string
			last_name: string
			phone_number: string
			email?: string
			customer_id: string
		}
	}>()
	const { productCategoryType } = useUtils()
	const { getPendingPolicies } = useProductActivationApi()
	const MINIMUM_VEHICLE_VALUE = 500000
	const { validateSerialNumber, validatePlateNumber, getVehicleMake } = useResourcesApi()
	const { StringToNumber, internationalNoFormat } = useCurrency()
	const productCategory = computed(() => productCategoryType(props.categoryType, props.productCode))
	const show = defineModel<boolean>({ default: false })

	const policyInfo = computed<ListContext>(() => ({
		'First Name': props.policyInfo?.first_name ?? '',
		'Last Name': props.policyInfo?.last_name ?? '',
		'Email Address': props.policyInfo?.email ?? '',
		'Phone Number': props.policyInfo?.phone_number ?? ''
	}))
	const fetchPendingSlots = async (e: object) => {
		if (props.purchaseId) {
			const result = await getPendingPolicies({
				purchase_id: String(props.purchaseId),
				is_used: false,
				...e
			})
			return result ?? null
		}
		return null
	}
	const editOptions = reactive({
		isCompleted: false,
		isExpanded: true,
		isEditable: true
	})
	const form = reactive({
		plate_number: '',
		vehicle_slot: undefined as string | undefined,
		gadget_slot: undefined as string | undefined,
		vehicle_manufacturer: undefined as string | undefined,
		vehicle_value: '',
		serial_number: '',
		record: {
			label: '',
			value: '0'
		}
	})
	const vehicleManufacturers = ref<SelectOptionItem[]>([])
	const validateValue = (_: unknown, value: number) => {
		const val = StringToNumber(value)
		if (val < 0) {
			return Promise.reject(new Error('Vehicle value can not be empty!'))
		} else if (val < MINIMUM_VEHICLE_VALUE) {
			return Promise.reject(new Error('Vehicle value must be minimum of 500,000!'))
		}
		return Promise.resolve()
	}
	const { resetFields } = useForm(form)

	const { handleScrollEffect, paginationFilter, data, getOptions } = useScrollPaginator({
		filterKey: 'temp_id',
		watch: computed(() => [props.purchaseId, show.value]),
		fetchOptions: fetchPendingSlots
	})
	const slotLabel = computed(() => {
		let label = ''
		let placeholder = ''
		let id = '' as 'vehicle_slot' | 'gadget_slot'
		if (productCategory.value?.isAuto) {
			label = 'Select Vehicle Slot'
			placeholder = 'Select vehicle slot to assign a policy holder'
			id = 'vehicle_slot'
		} else if (productCategory.value?.isGadget) {
			label = 'Select Gadget Slot'
			placeholder = 'Select gadget slot to assign a policy holder'
			id = 'gadget_slot'
		}
		return {
			label,
			placeholder,
			id,
			data: []
		}
	})

	const getSlotRecord = (_: SelectValue, fields: { item_worth: string; label: string } | DefaultOptionType) => {
		if (productCategory.value?.isComprehensive || productCategory.value?.isGadget) {
			form.record = {
				label: fields.label,
				value: fields.item_worth
			}
			editOptions.isCompleted = true
			editOptions.isEditable = false
		}
	}

	const sortOptionData = () => {
		const result = data.value
		return result.map(item => {
			const vType = item?.meta?.vehicle_type
			const vehicleTypeName =
				typeof vType === 'object' && vType && 'name' in vType ? String(vType.name) : String(vType ?? '')

			const vMake = item?.meta?.vehicle_make
			const vehicleMakeName =
				typeof vMake === 'object' && vMake && 'name' in vMake ? String(vMake.name) : String(vMake ?? '')

			const gType = item?.meta?.gadget_type
			const gadgetTypeName =
				typeof gType === 'object' && gType && 'name' in gType ? String(gType.name) : String(gType ?? '')

			const gBrand = item?.meta?.gadget_brand
			const gadgetBrandName =
				typeof gBrand === 'object' && gBrand && 'name' in gBrand ? String(gBrand.name) : String(gBrand ?? '')

			let label = `${vehicleTypeName} | ${vehicleMakeName || item?.meta?.vehicle_insurance_type || ''}`
			if (productCategory.value?.isGadget) {
				label = `${gadgetTypeName} | ${gadgetBrandName} | ${item?.meta?.gadget_model ?? ''}`
			}
			return {
				...item,
				item_worth: item?.meta?.vehicle_value ?? item?.meta?.gadget_value ?? 0,
				value: item.temp_id,
				label
			}
		})
	}
	const submitting = shallowRef(false)
	defineEmits<{
		(event: 'close'): void
	}>()
	const { reactivateEmployee } = useEmployeesApi()

	const submitForm = async (e: typeof form) => {
		let canSubmit = false
		try {
			submitting.value = true
			if (productCategory.value?.isGadget) {
				const response = await validateSerialNumber({ serial_number: e.serial_number })
				canSubmit = !response?.data?.exists
			} else if (productCategory.value?.isAuto) {
				const response = await validatePlateNumber({ plate_number: e.plate_number })
				canSubmit = !response?.data?.exists
			}
			if (canSubmit) {
				const temp_id = String(productCategory.value?.isAuto ? form.vehicle_slot : form.gadget_slot)

				const { gadget_slot, vehicle_slot, vehicle_value, ...rest } = e
				const value = vehicle_value ? { vehicle_value: StringToNumber(vehicle_value) } : {}
				const response = await reactivateEmployee({
					customer_id: props.customerId,
					purchase_id: props.purchaseId,
					payment_channel: 'wallet',
					sales_type: 'top up',
					transaction_id: '',
					temp_id,
					...rest,
					...value
				})
				if (response?.data) {
					refreshNuxtData('employee-details')
					useNotification().alert({
						status: 200,
						message: response.responseText
					})
				}
			} else {
				useNotification().alert({
					status: 400,
					message: `${productCategory.value?.isGadget ? 'Serial number' : 'Plate number'} already exists`
				})
			}
		} finally {
			submitting.value = false
		}
	}

	watch(show, async isOpen => {
		if (!isOpen) {
			data.value = []
		} else {
			if (!data.value.length) {
				resetFields()
				getOptions({ page: 1 })
				editOptions.isCompleted = false
				editOptions.isEditable = true
			}
			if (
				productCategory.value?.isAuto &&
				!productCategory.value?.isComprehensive &&
				!vehicleManufacturers.value.length
			) {
				const result = await getVehicleMake()
				if (Array.isArray(result)) {
					vehicleManufacturers.value = result
				}
			}
		}
	})
</script>

<template>
	<dialogs-error
		v-model:show="show"
		type="warning"
		header="Add Policy Holder"
		paragraph="Enter the policy holder manually"
		wrapper-class="bg-gray-50 px-4 py-8 rounded-md w-full space-y-5"
	>
		<template #p>
			<shared-list-item :lists="policyInfo" class="mb-8 mt-5 bg-purple-50" />
			<div class="">
				<div
					v-show="editOptions.isCompleted && (productCategory?.isComprehensive || productCategory?.isGadget)"
					class="mb-3 mt-5 flex min-h-[40px] w-full cursor-pointer flex-col items-center rounded-lg border border-[#DAF1EB] bg-success-25 px-5 py-3"
				>
					<div
						class="my-auto flex w-full items-center justify-between pt-1 transition-[500ms_ease-in-out]"
						:class="{ 'border-b pb-3': editOptions.isExpanded }"
					>
						<span class="text-xs font-medium capitalize text-gray-500">
							<span class="text-sm font-semibold text-primary-2">{{ form.record?.label ?? '' }} </span>
						</span>
						<button
							name="expand button"
							type="button"
							:aria-expanded="editOptions.isExpanded"
							@click="editOptions.isExpanded = !editOptions.isExpanded"
						>
							<lazy-tools-icon
								icon="caret-down-r"
								dir="arrows"
								:svg-class="editOptions.isExpanded ? 'rotate-180' : 'rotate-0'"
								:height="20"
								:width="20"
							/>
						</button>
					</div>

					<div
						class="flex w-full items-start justify-between overflow-y-auto transition-[500ms_ease-in-out]"
						:class="[!editOptions.isExpanded ? 'max-h-[0] opacity-0' : 'mt-3 max-h-[300px]']"
					>
						<div class="space-y-2">
							<span class="text-gray-500">
								{{ productCategory?.isAuto ? 'Vehicle' : 'Gadget' }} value
							</span>
							<p class="font-semibold text-gray-600">
								{{ internationalNoFormat(form.record.value, '₦') }}
							</p>
						</div>

						<button
							type="button"
							class="font-medium capitalize text-warning-500"
							@click="() => (editOptions.isEditable = !editOptions.isEditable)"
						>
							{{ editOptions.isEditable ? 'close' : 'edit' }}
						</button>
					</div>
				</div>

				<a-form :model="form" :validate-messages="antValidateMessages" @finish="submitForm">
					<div
						class="custom-transition-2 overflow-hidden"
						:class="{ 'max-h-[0]': !editOptions.isEditable, 'max-h-[1000px]': editOptions.isEditable }"
					>
						<a-form-item :label="slotLabel.label" :name="slotLabel.id" required>
							<lazy-a-select
								v-model:value="form[slotLabel.id]"
								:options="sortOptionData()"
								:name="slotLabel.id"
								:aria-label="slotLabel.label"
								:data-testid="slotLabel.id"
								:placeholder="slotLabel.placeholder"
								class="w-full min-w-[100px]"
								allow-clear
								:loading="paginationFilter.loading"
								@popup-scroll="handleScrollEffect"
								@change="getSlotRecord"
							/>
						</a-form-item>
					</div>

					<template v-if="productCategory?.isAuto">
						<div v-if="!productCategory?.isComprehensive">
							<a-form-item label="Vehicle Manufacturer" name="vehicle_manufacturer" required>
								<a-select
									v-model:value="form.vehicle_manufacturer"
									:options="vehicleManufacturers"
									show-search
									placeholder="Enter vehicle manufacturer"
								/>
							</a-form-item>
							<a-form-item
								label="Vehicle Value"
								name="vehicle_value"
								:rules="{ validator: validateValue }"
							>
								<a-input
									v-model:value="form.vehicle_value"
									v-naira="true"
									placeholder="Enter vehicle value"
									class="custom-input"
								/>
							</a-form-item>
						</div>

						<a-form-item label="Plate number" name="plate_number" :rules="{ required: true, min: 3 }">
							<a-input
								v-model:value="form.plate_number"
								placeholder="Enter plate number"
								class="custom-input"
							/>
						</a-form-item>
					</template>

					<a-form-item
						v-if="productCategory?.isGadget"
						label="Serial Number"
						name="serial_number"
						:rules="{
							required: true,
							min: 3
						}"
					>
						<a-input
							v-model:value="form.serial_number"
							placeholder="Enter gadget serial number"
							class="custom-input"
						/>
					</a-form-item>

					<div class="flex w-full flex-wrap items-center justify-between gap-x-8 gap-y-3 border-t-2 pt-8">
						<button
							type="button"
							name="back to plan selection"
							class="default-btn flex-1 justify-center bg-gray-200"
							@click="$emit('close')"
						>
							Back
						</button>
						<a-button
							html-type="submit"
							:loading="submitting"
							:disbaled="submitting"
							name="submit policy"
							class="default-btn flex-1 justify-center !bg-primary-2 !text-white"
						>
							Add Policy Holder
						</a-button>
					</div>
				</a-form>
			</div>
		</template>
		<template #button>
			<span />
		</template>
	</dialogs-error>
</template>

<style scoped>
	:deep(.ant-input) {
		@apply border-gray-200 !bg-gray-50;
	}
	.custom-input {
		@apply !px-5;
	}
</style>
